import { useEffect, useMemo, useRef, useState } from "react";
import { useTranslation } from "react-i18next";

const DEFAULT_MAX_FILE_SIZE = 10 * 1024 * 1024; // 10MB
const DEFAULT_ALLOWED = ["image/jpeg", "image/png", "image/webp", "application/pdf"];

// ✅ definicija regexa van rendera
const phoneRe = /^[0-9+()\s-]{6,}$/;
const emailRe = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;


function readUtmFromUrl() {
  const params = new URLSearchParams(window.location.search);
  return {
    utm_source: params.get("utm_source") || "",
    utm_medium: params.get("utm_medium") || "",
    utm_campaign: params.get("utm_campaign") || "",
    utm_content: params.get("utm_content") || "",
    utm_term: params.get("utm_term") || "",
    gclid: params.get("gclid") || "",
    qr: params.get("qr") || "",
    qr_id: params.get("qr_id") || "",
    code: params.get("code") || "",
  };
}

/**
 * useLeadForm
 * - mailserverApi: "https://modularobjects.com/mailserver"
 * - context: objekat koji želiš da pošalješ backendu (npr. { page:"projects", projectId, projectTitle })
 * - conversion: opcioni callback (npr. gtag conversion)
 */
export function useLeadForm({
  mailserverApi,
  context = {},
  conversion,
  allowAttachments = true,
  maxFileSize = DEFAULT_MAX_FILE_SIZE,
  allowedTypes = DEFAULT_ALLOWED,
  initialForm = { name: "", phone: "", email: "", message: "", website: "" },
}) {
  const [form, setForm] = useState(initialForm);
  const { i18n } = useTranslation();

  const [utm, setUtm] = useState({
    utm_source: "",
    utm_medium: "",
    utm_campaign: "",
    utm_content: "",
    utm_term: "",
    gclid: "",
    qr: "",
    qr_id: "",
    code: "",
  });

  const [files, setFiles] = useState([]);
  const [submitting, setSubmitting] = useState(false);
  const [done, setDone] = useState(false);
  const [error, setError] = useState("");
  const [apiResult, setApiResult] = useState(null);

  const firedRef = useRef(false);

  useEffect(() => {
    setUtm(readUtmFromUrl());
  }, []);

  const validation = useMemo(() => {
    const nameOk = form.name.trim().length >= 2;
    const phoneOk = phoneRe.test(form.phone.trim());
    const emailOk = !form.email?.trim() || emailRe.test(form.email.trim());
    return { nameOk, phoneOk, emailOk };
  }, [form.name, form.phone, form.email]);

  const canSubmit = useMemo(() => {
    return validation.nameOk && validation.phoneOk && validation.emailOk && !submitting;
  }, [validation, submitting]);

  function onPick(e) {
    const picked = Array.from(e.target.files || []);
    const safe = [];

    for (const f of picked) {
      if (f.size > maxFileSize) {
        alert(`${f.name}: >${Math.round(maxFileSize / (1024 * 1024))}MB`);
        continue;
      }
      if (!allowedTypes.includes(f.type)) {
        alert(`${f.name}: nedozvoljen tip`);
        continue;
      }
      safe.push(f);
    }

    setFiles(prev => [...prev, ...safe].slice(0, 10));
    e.target.value = "";
  }

  function removeFile(idx) {
    setFiles(prev => prev.filter((_, i) => i !== idx));
  }

  async function submit(e) {
    e?.preventDefault?.();

    // honeypot
    if (form.website?.length > 0) {
      setError("Greška pri slanju. Pokušajte ponovo.");
      return;
    }

    if (!canSubmit) {
      setError("Molimo popunite obavezna polja ispravno.");
      return;
    }

    setSubmitting(true);
    setError("");
    setApiResult(null);
    setDone(false);

    const payload = {
      code: utm.code,
      lang: (i18n?.language || "sr").split("-")[0],
      name: form.name.trim(),
      email: form.email?.trim() || null,
      phone: form.phone?.trim() || null,
      message: form.message?.trim() || null,
      utm_source: utm.utm_source,
      utm_medium: utm.utm_medium,
      utm_campaign: utm.utm_campaign,
      utm_content: utm.utm_content,
      utm_term: utm.utm_term,
      gclid: utm.gclid,
      qr: utm.qr,
      qr_id: utm.qr_id,
      page_url: window.location.href,
      context, // ✅ bitno: ovde šalješ “odakle dolazi” (projects/kitchens/…)
    };

    try {
      const controller = new AbortController();
      const t = setTimeout(() => controller.abort(), 15000);

      const response = await fetch(`${mailserverApi}/api/leads`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(payload),
        credentials: "include",
        signal: controller.signal,
      });

      clearTimeout(t);

      let result = null;
      let rawText = "";
      try {
        result = await response.json();
      } catch {
        rawText = await response.text().catch(() => "");
      }

      if (!response.ok || !result?.ok) {
        const msg = result?.message || rawText || `HTTP ${response.status}`;
        throw new Error(msg);
      }

      setApiResult(result);

      // ✅ konverzija odmah (pre upload-a)
      if (!firedRef.current) {
        conversion?.(result);
        firedRef.current = true;
      }

      const leadId = result?.id;

      if (allowAttachments && leadId && files.length > 0) {
        const fd = new FormData();
        files.forEach(f => fd.append("files", f));

        const up = await fetch(`${mailserverApi}/api/leads/${leadId}/files`, {
          method: "POST",
          body: fd,
          credentials: "include",
        });

        let upJson = null, upTxt = "";
        try { upJson = await up.json(); } catch { upTxt = await up.text().catch(() => ""); }

        if (!up.ok || upJson?.ok === false) {
          console.warn("Upload nije uspeo:", upJson?.message || upTxt || up.status);
        }
      }

      setDone(true);
      setForm(initialForm);
      setFiles([]);
      firedRef.current = false; // dozvoli novi submit

    } catch (err) {
      console.error("Lead submit error:", err);
      setError("Došlo je do greške. Pokušajte ponovo ili nas pozovite.");
    } finally {
      setSubmitting(false);
    }
  }

  return {
    form, setForm,
    utm,
    files, onPick, removeFile,
    submitting, done, error, apiResult,
    canSubmit,
    submit,
    validation,
  };
}
